import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Beef } from "lucide-react";
import { cn, ageLabel, CATEGORY_LABEL } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

const SEX_LABEL = { female: "Hembra", male: "Macho" };
const STATUS = {
  active:      { label: "Activo",      variant: "present"  },
  sold:        { label: "Vendido",     variant: "muted"    },
  slaughtered: { label: "Sacrificado", variant: "missing"  },
  dead:        { label: "Muerto",      variant: "missing"  },
  lost:        { label: "Extraviado",  variant: "ochre"    },
  in_transit:  { label: "En tránsito", variant: "ochre"    },
  transferred: { label: "Trasladado",  variant: "ochre"    },
};

export function AnimalListRow({ animal, to, selectMode, selected, onToggleSelect }) {
  const navigate = useNavigate();
  const status = STATUS[animal.status] || STATUS.active;
  const href = to || `/animales/${animal.id}`;

  const rowClass = cn(
    "group w-full text-left flex items-center gap-3 px-3 sm:px-4 py-3 sm:py-3.5",
    "border-b border-border last:border-0",
    "hover:bg-muted/60 transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
    selectMode && selected && "bg-primary/5"
  );

  const rowContent = (
    <>
      {selectMode && (
        <Checkbox
          checked={!!selected}
          onCheckedChange={() => onToggleSelect?.(animal.id)}
          onClick={(e) => e.stopPropagation()}
          data-testid={`animal-select-checkbox-${animal.id}`}
          className="shrink-0"
        />
      )}
      <div className="w-10 h-10 rounded-[calc(var(--radius)-0.35rem)] bg-accent text-accent-foreground grid place-items-center shrink-0">
        <Beef size={17} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            data-testid="animal-list-lomo"
            className="font-heading text-[15px] font-semibold text-foreground tracking-tight"
          >
            Lomo {animal.lomo_number || "—"}
          </span>
          <Badge variant={status.variant} data-testid="animal-row-status">{status.label}</Badge>
          {animal.is_sick && <Badge variant="ochre" data-testid="animal-row-sick-badge">Salud</Badge>}
          {animal.is_elite && <Badge variant="warm" data-testid="animal-row-elite-badge">Élite</Badge>}
          {animal.is_good_mother && <Badge variant="present" data-testid="animal-row-good-mother-badge">Buena madre</Badge>}
        </div>
        <div className="text-[12px] text-muted-foreground flex flex-wrap items-center gap-x-1.5 mt-0.5">
          <span>{CATEGORY_LABEL[animal.category] || "Sin categoría"}</span>
          <span aria-hidden>·</span>
          <span>{SEX_LABEL[animal.sex] || "-"}</span>
          <span aria-hidden>·</span>
          <span>{ageLabel(animal.birth_date)}</span>
        </div>
        <div
          data-testid="animal-list-rfid"
          className="font-mono text-[11px] text-muted-foreground tracking-[0.05em] mt-1 tabular-nums truncate"
        >
          {animal.chip_code || "sin chip"}
        </div>
      </div>
      {!selectMode && <ChevronRight size={16} className="text-muted-foreground/70 shrink-0 group-hover:text-primary transition-colors duration-150" />}
    </>
  );

  if (selectMode) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onToggleSelect?.(animal.id)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleSelect?.(animal.id); } }}
        data-testid="animal-list-row"
        className={rowClass}
      >
        {rowContent}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate(href)}
      data-testid="animal-list-row"
      className={rowClass}
    >
      {rowContent}
    </button>
  );
}
